import { useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { ClientAppEvent } from "constants/index";
import { useWriterComponentState } from "hooks";

import { OTHER_REASON_VALUE, REPORT_COPY, REPORT_REASONS } from "./constants";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  resourceType: FlaggableResourceType;
  resourceId: number;
}

// Deliberately says nothing about who authored the reported resource: naming
// them here would hand every viewer the author's identity, which the carrier
// Q&A payload withholds on purpose.
export function ReportResourceModal(props: Props) {
  const { open, onClose, resourceType, resourceId } = props;
  const posthog = usePostHog();
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");

  const {
    write: reportResource,
    error,
    loading,
    success: reportSubmitted,
    resetState,
  } = useWriterComponentState({
    errorMessage:
      "Failed to submit report. Please contact us if the problem persists.",
    writeConfig: {
      method: "post",
      url: "/flags",
    },
  });

  useEffect(() => {
    if (open) {
      posthog?.capture(ClientAppEvent.REPORT_MODAL_OPENED, {
        resource_type: resourceType,
      });
    }
  }, [open, posthog, resourceType]);

  const reason =
    selectedReason === OTHER_REASON_VALUE
      ? otherReason.trim()
      : selectedReason.trim();

  const onModalClose = () => {
    setSelectedReason("");
    setOtherReason("");
    resetState();
    onClose();
  };

  const onReportSubmitted = () => {
    // The reason itself is deliberately not sent: it is free text where users
    // paste the very content they are reporting. Flag.metadata is its record.
    posthog?.capture(ClientAppEvent.REPORT_SUBMITTED, {
      resource_type: resourceType,
      has_reason: reason !== "",
    });
  };

  const submitReport = () => {
    reportResource(onReportSubmitted, {
      resource_type: resourceType,
      resource_id: resourceId,
      reason,
    });
  };

  const { title, prompt } = REPORT_COPY[resourceType];

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onModalClose}>
      {reportSubmitted ? (
        <>
          <DialogTitle variant="h4">
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon color="success" />
              Report Submitted
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack direction="column" spacing={1}>
              <Typography variant="body1">
                Our team will look into it.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reports are confidential.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={onModalClose}>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle variant="h4">{title}</DialogTitle>
          <DialogContent>
            <LoadingWrappedNode loading={loading}>
              <Stack direction="column" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {prompt}
                </Typography>
                <FormControl>
                  <RadioGroup
                    value={selectedReason}
                    onChange={(event) => setSelectedReason(event.target.value)}
                  >
                    {REPORT_REASONS.map((reportReason) => (
                      <FormControlLabel
                        key={reportReason}
                        value={reportReason}
                        control={<Radio />}
                        label={reportReason}
                      />
                    ))}
                    <FormControlLabel
                      value={OTHER_REASON_VALUE}
                      control={<Radio />}
                      label="Other"
                    />
                  </RadioGroup>
                </FormControl>
                {selectedReason === OTHER_REASON_VALUE && (
                  <TextField
                    id="report-reason"
                    variant="outlined"
                    multiline
                    minRows={3}
                    placeholder="Tell us what happened"
                    inputProps={{ "aria-label": "Report reason" }}
                    onChange={(event) => setOtherReason(event.target.value)}
                    value={otherReason}
                  />
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  <LockIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Reports are confidential.
                  </Typography>
                </Stack>
                <ErrorTypography error={error} />
              </Stack>
            </LoadingWrappedNode>
          </DialogContent>
          {!loading && (
            <DialogActions>
              <Button onClick={onModalClose}>Go Back</Button>
              <Button
                autoFocus
                variant="contained"
                color="error"
                onClick={submitReport}
              >
                {error == null ? "Submit Report" : "Retry"}
              </Button>
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
}
